/**
 * AppSwitcher — 9-dot cross-product launcher
 *
 * Renders a 9-dot icon in the host product's nav bar. On click, opens
 * a popover listing the user's other entitled BOXX apps (plus custom
 * tiles like WhatsApp). Clicking a tile navigates to that product
 * (silent SSO handoff is a separate spec; for v0 tiles are plain links).
 *
 * Data source: SaaSBOXX GET /api/v1/me/apps/ — see
 * c:/SaasBOXX/docs/ENTITLEMENT_API_SPEC.md.
 *
 * Auth (v0): reads a SaaSBOXX tenant-portal JWT from localStorage key
 * "saasboxx_token". When absent, renders MOCK_DATA so the UX is still
 * testable without the full SSO plumbing in place. Once the
 * cross-product session story (SSO_HANDOFF_SPEC) ships, the token
 * will be populated automatically on first SSO arrival.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Settings, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';

// The widget now calls a SAME-ORIGIN proxy endpoint. The product's
// backend forwards to SaaSBOXX server-side using its
// INTER_SERVICE_SECRET, so the user's browser doesn't need a SaaSBOXX
// token. Auth is the user's existing local product JWT.
// LabBOXX's proxy vhost routes /v1/ to the backend but not /api/v1/ —
// /api/v1/ falls through to the SPA and 405s on the GET. The backend
// mounts the entitlement proxy at both prefixes; use /v1/ to match the
// proxy.
const ENTITLEMENT_PATH = '/v1/auth/me/apps/';
const SAASBOXX_BASE = '';  // unused — SaaSBOXX returns absolute icon URLs
const TOKEN_LS_KEYS = ['jwt_access_token', 'access_token', 'token', 'authToken', 'saasboxx_token'];  // LabBOXX uses jwt_access_token (see providers/authProvider.ts); rest are fallbacks for parity
const CACHE_LS_KEY = 'app_switcher_cache_v1';
const CACHE_MAX_AGE_MS = 10 * 60 * 1000;

type AppTile = {
  slug: string;
  name: string;
  tagline: string | null;
  icon: string | null;
  icon_url: string | null;
  accent_color: string | null;
  url: string;
  sso_handoff_url: string | null;
  status: 'active' | 'suspended' | 'custom';
  opens_in: 'same_tab' | 'new_tab';
  order: number;
};

type EntitlementResponse = {
  tenant: { slug: string; name: string; logo_url: string | null };
  user: {
    id: string;
    name: string;
    email: string;
    is_tenant_admin: boolean;
    avatar_url: string | null;
  };
  apps: AppTile[];
  admin_console_url: string;
  revision: string;
};

const MOCK_DATA: EntitlementResponse = {
  tenant: { slug: 'demo', name: 'Demo Tenant', logo_url: null },
  user: { id: 'mock', name: 'Demo User', email: 'demo@example.com', is_tenant_admin: true, avatar_url: null },
  apps: [
    { slug: 'wageboxx', name: 'WageBOXX', tagline: 'Payroll & HR', icon: null, icon_url: null, accent_color: '#2563eb', url: '#', sso_handoff_url: null, status: 'active', opens_in: 'same_tab', order: 10 },
    { slug: 'petsboxx', name: 'PetsBOXX', tagline: 'Care for Pets', icon: null, icon_url: null, accent_color: '#faad14', url: '#', sso_handoff_url: null, status: 'active', opens_in: 'same_tab', order: 20 },
    { slug: 'queueboxx', name: 'QueueBOXX', tagline: 'Smart Queues', icon: null, icon_url: null, accent_color: '#10b981', url: '#', sso_handoff_url: null, status: 'active', opens_in: 'same_tab', order: 30 },
    { slug: 'labboxx', name: 'LabBOXX', tagline: 'Lab Operations', icon: null, icon_url: null, accent_color: '#1890ff', url: '#', sso_handoff_url: null, status: 'active', opens_in: 'same_tab', order: 40 },
  ],
  admin_console_url: 'https://admin.onprosoft.com',
  revision: 'mock',
};

type CacheEntry = { fetchedAt: number; etag: string | null; body: EntitlementResponse };

function readCache(): CacheEntry | null {
  try {
    const raw = sessionStorage.getItem(CACHE_LS_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry;
    if (Date.now() - entry.fetchedAt > CACHE_MAX_AGE_MS) return null;
    return entry;
  } catch {
    return null;
  }
}

function writeCache(entry: CacheEntry) {
  try { sessionStorage.setItem(CACHE_LS_KEY, JSON.stringify(entry)); } catch { /* quota errors are fine */ }
}

function readLocalToken(): string | null {
  for (const k of TOKEN_LS_KEYS) {
    const v = localStorage.getItem(k);
    if (v) return v;
  }
  return null;
}

async function fetchApps(token: string): Promise<{ body: EntitlementResponse; etag: string | null } | { error: string }> {
  const cached = readCache();
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (cached?.etag) headers['If-None-Match'] = cached.etag;

  let resp: Response;
  try {
    // Call same-origin proxy. The product's backend forwards to
    // SaaSBOXX using its INTER_SERVICE_SECRET (server-side).
    resp = await fetch(ENTITLEMENT_PATH, { headers });
  } catch (e) {
    return { error: 'network' };
  }

  if (resp.status === 304 && cached) {
    return { body: cached.body, etag: cached.etag };
  }
  if (resp.status === 401) return { error: 'unauthorized' };
  if (resp.status === 403) return { error: 'forbidden' };
  if (resp.status === 404) return { error: 'no_user_for_tenant' };
  if (!resp.ok) return { error: `http_${resp.status}` };

  const body = (await resp.json()) as EntitlementResponse;
  const etag = resp.headers.get('ETag');
  return { body, etag };
}

function NineDotIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={cn('h-5 w-5', className)} aria-hidden="true">
      {[3, 10, 17].flatMap(y => [3, 10, 17].map(x => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r={1.6} />
      )))}
    </svg>
  );
}

function TileIcon({ tile }: { tile: AppTile }) {
  if (tile.icon_url) {
    return (
      <img
        src={tile.icon_url.startsWith('http') ? tile.icon_url : `${SAASBOXX_BASE}${tile.icon_url}`}
        alt=""
        className="h-8 w-8 rounded object-contain"
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  if (tile.icon) return <span className="text-2xl leading-none">{tile.icon}</span>;
  const accent = tile.accent_color || '#6b7280';
  return (
    <div
      className="h-8 w-8 rounded flex items-center justify-center text-white text-sm font-semibold"
      style={{ backgroundColor: accent }}
    >
      {tile.name.charAt(0)}
    </div>
  );
}

export function AppSwitcher({ currentProductSlug = 'wageboxx' }: { currentProductSlug?: string }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<EntitlementResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingMock, setUsingMock] = useState(false);
  const inFlight = useRef(false);

  // Prime from cache synchronously on mount so the popover never flashes empty.
  useEffect(() => {
    const cached = readCache();
    if (cached) setData(cached.body);
  }, []);

  // Fetch on first open (and refresh in background if cache is warm).
  useEffect(() => {
    if (!open || inFlight.current) return;
    const token = readLocalToken();
    if (!token) {
      setUsingMock(true);
      setData(MOCK_DATA);
      return;
    }
    setUsingMock(false);
    setLoading(!data);
    inFlight.current = true;
    fetchApps(token)
      .then(result => {
        if ('error' in result) {
          setError(result.error);
          return;
        }
        setData(result.body);
        setError(null);
        writeCache({ fetchedAt: Date.now(), etag: result.etag, body: result.body });
      })
      .finally(() => {
        inFlight.current = false;
        setLoading(false);
      });
  }, [open]);

  const tiles = useMemo(() => data?.apps ?? [], [data]);

  // Keep the switcher whenever it can still DO something: switch to another
  // app, open a custom tile, or reach Account Management. Only hide it when a
  // non-admin has a single app and nothing else actionable — a tenant admin
  // with one subscription still needs the 9-dot to reach "Manage account".
  const otherAppCount = tiles.filter(t => t.slug !== currentProductSlug && t.status !== 'suspended').length;
  const hasCustomTiles = tiles.some(t => t.status === 'custom');
  const canManageAccount = !!(data?.user?.is_tenant_admin && data?.admin_console_url);
  if (data && !usingMock && otherAppCount === 0 && !hasCustomTiles && !canManageAccount) return null;

  // "Manage account" → mint a portal-handoff token via LabBOXX backend,
  // then open the SaaSBOXX tenant portal at the role-appropriate page.
  // Without this, a plain <a href={admin_console_url}> dumps the user
  // at <slug>.saas-boxx.com with no session → /login.
  const handleManageAccountClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    if (!data?.admin_console_url) return;
    const fallback = () => window.open(data.admin_console_url!, '_blank', 'noopener,noreferrer');
    const isAdmin = !!data.user?.is_tenant_admin;
    const next = isAdmin ? '/dashboard' : '/access';
    const localToken = readLocalToken();
    if (!localToken) { fallback(); return; }
    try {
      const resp = await fetch('/api/v1/auth/sso/issue-portal-token/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localToken}` },
        body: JSON.stringify({ next }),
      });
      if (!resp.ok) { fallback(); return; }
      const body = await resp.json() as { redirect_url?: string };
      if (!body.redirect_url) { fallback(); return; }
      window.open(body.redirect_url, '_blank', 'noopener,noreferrer');
    } catch {
      fallback();
    }
  };

  const handleTileClick = async (tile: AppTile, e: React.MouseEvent) => {
    if (tile.status === 'suspended') { e.preventDefault(); return; }
    if (tile.slug === currentProductSlug) { e.preventDefault(); setOpen(false); return; }
    if (tile.status === 'custom' || !tile.sso_handoff_url) {
      setOpen(false);
      return;
    }

    // Silent SSO handoff. Mint a federation token via this product's
    // own backend, then redirect to the target's accept URL.
    e.preventDefault();
    setOpen(false);

    const localToken = readLocalToken();
    if (!localToken) {
      window.location.href = tile.url;
      return;
    }

    try {
      // LabBOXX's proxy vhost routes /v1/ to the backend but not /api/v1/
      // (the latter falls through to the SPA, which 405s on POST). The
      // backend mounts the handoff endpoint at both prefixes — use /v1/.
      const resp = await fetch('/v1/auth/sso/handoff/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localToken}`,
        },
        body: JSON.stringify({ target: tile.slug }),
      });
      if (!resp.ok) {
        console.warn('[AppSwitcher] handoff mint failed', resp.status);
        window.location.href = tile.url;
        return;
      }
      const data = await resp.json() as { token?: string };
      if (!data.token) {
        window.location.href = tile.url;
        return;
      }
      // Don't pass next=/ — some products redirect '/' to /login when on a
      // tenant subdomain. Let the target's SsoLanding fall through to its
      // own default landing page (typically /dashboard).
      const u = new URL(tile.sso_handoff_url);
      u.searchParams.set('token', data.token);
      window.location.href = u.toString();
    } catch (err) {
      console.warn('[AppSwitcher] handoff failed', err);
      window.location.href = tile.url;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="header-icon-btn" aria-label="Switch app">
              <NineDotIcon />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Switch app</TooltipContent>
      </Tooltip>

      <PopoverContent align="end" className="w-80 p-0" sideOffset={8}>
        {data && (
          <div className="px-4 pt-3 pb-2 border-b">
            <div className="text-xs text-muted-foreground truncate">{data.tenant.name}</div>
            <div className="text-sm font-semibold">Your apps</div>
            {usingMock && (
              <div className="mt-1 text-[10px] text-warning-700">
                Showing demo apps — sign in to load your real entitlements.
              </div>
            )}
          </div>
        )}

        {loading && !data && (
          <div className="p-4 grid grid-cols-2 gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-20 bg-muted/50 rounded animate-pulse" />
            ))}
          </div>
        )}

        {error && (
          <div className="p-4 text-sm text-destructive">
            {error === 'unauthorized' && 'Session expired — please sign in again.'}
            {error === 'forbidden' && 'You have no active product access at this tenant.'}
            {error === 'no_user_for_tenant' && 'No SaaSBOXX directory record found for this account. Ask an admin to provision you.'}
            {error === 'network' && 'Network error — try again.'}
            {!['unauthorized', 'forbidden', 'network', 'no_user_for_tenant'].includes(error) && `Error: ${error}`}
          </div>
        )}

        {data && (
          <>
            <div className="p-3 grid grid-cols-2 gap-2">
              {tiles.map(tile => {
                const isCurrent = tile.slug === currentProductSlug;
                const isSuspended = tile.status === 'suspended';
                const isCustom = tile.status === 'custom';
                const tileClasses = cn(
                  'group relative flex flex-col items-start gap-1.5 rounded-lg border p-3 transition-all',
                  isCurrent && 'bg-accent/40 cursor-default',
                  isSuspended && 'opacity-50 cursor-not-allowed',
                  !isCurrent && !isSuspended && 'hover:border-foreground/30 hover:shadow-sm cursor-pointer',
                );
                const content = (
                  <>
                    <div className="flex w-full items-center justify-between">
                      <TileIcon tile={tile} />
                      {isCustom && <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />}
                    </div>
                    <div className="text-sm font-medium leading-tight truncate w-full" title={tile.name}>
                      {tile.name}
                    </div>
                    {tile.tagline && (
                      <div className="text-[10px] text-muted-foreground line-clamp-1 w-full">{tile.tagline}</div>
                    )}
                    {isCurrent && (
                      <div
                        className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: tile.accent_color || '#10b981' }}
                        aria-label="Current"
                      />
                    )}
                  </>
                );
                if (isCurrent || isSuspended) {
                  return <div key={tile.slug} className={tileClasses} aria-disabled={isSuspended}>{content}</div>;
                }
                return (
                  <a
                    key={tile.slug}
                    href={tile.url}
                    target={tile.opens_in === 'new_tab' ? '_blank' : '_self'}
                    rel={tile.opens_in === 'new_tab' ? 'noopener noreferrer' : undefined}
                    className={tileClasses}
                    onClick={(e) => handleTileClick(tile, e)}
                  >
                    {content}
                  </a>
                );
              })}
            </div>

            {data.user.is_tenant_admin && data.admin_console_url && (
              <a
                href={data.admin_console_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 text-sm border-t hover:bg-accent transition-colors"
                onClick={handleManageAccountClick}
              >
                <Settings className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1">
                  <div className="font-medium">Manage account</div>
                  <div className="text-[10px] text-muted-foreground">Billing, team, subscriptions</div>
                </div>
                <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />
              </a>
            )}

            <div className="flex items-center justify-between px-4 py-2 border-t bg-muted/30 text-xs">
              <div className="truncate">
                <div className="font-medium truncate">{data.user.name}</div>
                <div className="text-muted-foreground truncate">{data.user.email}</div>
              </div>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default AppSwitcher;
